/**
 * Tabbed Product Component
 * Handles product tab switching and color swatch image updates
 */

class TabbedCollection extends HTMLElement {
  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.teardown();
  }

  init() {
    this.teardown();

    this.tabs = this.querySelectorAll('.tabbed-collection__tab');
    this.panels = this.querySelectorAll('.tabbed-collection__panel');

    this.onTabClick = (event) => {
      const tab = event.currentTarget;
      const index = Number(tab.dataset.index);
      if (!Number.isNaN(index)) this.switchTab(index);
    };

    this.onTabKeydown = (event) => {
      const currentIndex = Number(event.currentTarget.dataset.index);
      if (Number.isNaN(currentIndex)) return;

      let nextIndex = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % this.tabs.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = this.tabs.length - 1;
      }

      if (nextIndex === null) return;
      event.preventDefault();
      this.switchTab(nextIndex);
      this.tabs[nextIndex]?.focus();
    };

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.onTabClick);
      tab.addEventListener('keydown', this.onTabKeydown);
    });

    this.onSwatchClick = (event) => {
      const swatch = event.target.closest('.tabbed-collection__swatch');
      if (!swatch || !this.contains(swatch)) return;
      this.selectSwatch(swatch);
    };

    this.addEventListener('click', this.onSwatchClick);
  }

  teardown() {
    if (this.tabs) {
      this.tabs.forEach((tab) => {
        tab.removeEventListener('click', this.onTabClick);
        tab.removeEventListener('keydown', this.onTabKeydown);
      });
    }

    if (this.onSwatchClick) {
      this.removeEventListener('click', this.onSwatchClick);
    }
  }

  switchTab(index) {
    this.tabs.forEach((tab, i) => {
      const isActive = i === index;
      tab.classList.toggle('active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    this.panels.forEach((panel, i) => {
      const isActive = i === index;
      panel.classList.toggle('active', isActive);
      if (isActive) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });
  }

  selectSwatch(swatch) {
    const panel = swatch.closest('.tabbed-collection__panel');
    if (!panel) return;

    const imageUrl = swatch.dataset.imageUrl;
    const imageSrcset = swatch.dataset.imageSrcset;
    if (!imageUrl) return;

    const img = panel.querySelector('.tabbed-collection__product-img');
    if (!img || img.tagName !== 'IMG') return;

    panel.querySelectorAll('.tabbed-collection__swatch').forEach((item) => {
      const isActive = item === swatch;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    img.classList.add('is-switching');

    const applyImage = () => {
      img.src = imageUrl;
      if (imageSrcset) {
        img.srcset = imageSrcset;
      }
      img.classList.remove('is-switching');
    };

    window.setTimeout(applyImage, 120);
  }
}

if (!customElements.get('tabbed-collection')) {
  customElements.define('tabbed-collection', TabbedCollection);
}

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('tabbed-collection').forEach((element) => {
    if (typeof element.init === 'function') element.init();
  });
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('tabbed-collection').forEach((element) => {
    if (typeof element.teardown === 'function') element.teardown();
  });
});
